import type { UI } from '@/v2/types';
import { useState } from 'react';
import styled from '@emotion/styled';
import { Box, Flex, Hide, NakedButton, Select } from '@qga/roo-ui/components';
import BlockContent from '@sanity/block-content-to-react';
import { themeGet } from 'styled-system';

import Disclosure from '@/v2/../components/Disclosure';
import Heading from '@/v2/../components/Heading';
import Link from '@/v2/../components/Link';
import Text from '@/v2/../components/Text';
// Legacy components
import withProps from '@/v2/../components/withProps';
import { Divider } from '@/v2/components/ui/Divider';
import { useGA4Events } from '@/v2/hooks/featureFlags/ga4Events/useGA4Events';
import { dataLayer } from '@/v2/utils/dataLayer';

const Button = styled(NakedButton)`
  width: 100%;
  text-align: left;
  padding-top: 22px;
  padding-bottom: 17px;
  box-sizing: border-box;

  border-bottom: 4px solid;

  border-color: ${({ selected }: { selected: boolean }) =>
    selected ? themeGet('colors.primary') : `transparent`};

  :hover {
    background-color: ${themeGet('colors.lightGrey')};
  }

  :focus {
    outline: none;
  }
`;

interface Props {
  categories: Array<UI.FaqCategory>;
}

const AllCategories = ({ categories = [] }: Props) => {
  const shouldUseNewEvents = useGA4Events();
  const [questionSlug, setQuestionSlug] = useState<string | null>();

  const [selectedCategory, setSelectedCategory] =
    useState<UI.FaqCategory | null>(categories?.[0]);

  const onSelected = (cat: UI.FaqCategory | undefined) => {
    cat && setSelectedCategory(cat);
  };

  return (
    <Flex
      my={[0, null, 8]}
      flexDirection={['column', null, 'row']}
      data-testid="ALL_CATEGORIES"
    >
      <Hide xs sm>
        <Box minWidth={'336px'}>
          <Box mb={4}>
            <Heading.h5>Topics</Heading.h5>
          </Box>
          {categories?.map((cat) => (
            <Flex
              key={cat.slug}
              alignItems="center"
              my={2}
              data-testid={`CATEGORY_${cat.slug}`}
            >
              <Button
                selected={selectedCategory?.slug === cat.slug}
                onClick={() => onSelected(cat)}
              >
                <Heading.h5
                  color={selectedCategory?.slug === cat.slug && 'primary'}
                >
                  {cat.name}
                </Heading.h5>
              </Button>
            </Flex>
          ))}
        </Box>
      </Hide>
      <Hide md lg>
        <Box mb={1}>
          <Text fontWeight="bold" fontSize="xs">
            Select a Topic
          </Text>
        </Box>
        <Box>
          <Select
            fontSize="xs"
            value={selectedCategory?.slug}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
              onSelected(categories.find((cat) => cat.slug === e.target.value));
            }}
          >
            {categories?.map((cat) => (
              <option key={cat.slug} value={cat.slug}>
                {cat.name}
              </option>
            ))}
          </Select>
        </Box>
        <Box mx="-15px" my={6}>
          <Divider />
        </Box>
      </Hide>
      <Box flex={1}>
        {selectedCategory && (
          <Box ml={[0, null, 10]} data-testid="CATEGORY_SELECTED">
            <Box>
              <Heading.h4>{selectedCategory.name}</Heading.h4>
            </Box>
            {selectedCategory.faqs?.map((faq) => (
              <Box
                key={faq.question.slug}
                width="100%"
                data-testid={`CATEGORY_SELECTED_${faq.question.slug}`}
                onClick={() => {
                  if (
                    shouldUseNewEvents &&
                    faq.question.slug !== questionSlug
                  ) {
                    setQuestionSlug(faq.question.slug);
                    dataLayer.accordionOpenEvent({
                      groupName: 'FAQ Page',
                      itemName: faq.question.name,
                    });
                  }
                }}
              >
                <Disclosure
                  headerTitle={faq.question.name}
                  variant="default"
                  fontFamily={null}
                  headerStyles={{
                    fontSize: ['sm', null, 'base'],
                    pt: 3,
                    pb: 1,
                  }}
                  expand={false}
                >
                  <BlockContent
                    blocks={faq.answer}
                    serializers={{
                      types: {
                        block: withProps(
                          ({ node }: { node: { style: string } }) => ({
                            ...(node.style === 'normal' && {
                              fontSize: 'sm',
                              display: 'inline-block',
                              lineHeight: 1.5,
                            }),
                          }),
                        )(Text),
                      },
                      marks: {
                        link: withProps(
                          ({ mark }: { mark: { href: string } }) => ({
                            href: mark.href,
                            external: true,
                          }),
                        )(Link.Text),
                      },
                    }}
                  />
                </Disclosure>
              </Box>
            ))}
          </Box>
        )}
      </Box>
    </Flex>
  );
};

export default AllCategories;
